import { Box, Typography, Card, CardContent } from "@mui/material";
import { AsyncButton } from "src/components/common/buttons/AsyncButton";
import { getAddableTreeItemSubConfiguratorComponents } from "./treeItemModel/configuratorTreeItemQueries";
import { mergeSx } from "src/utils/styles.ts";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { AddSubConfiguratorsToTreeViewProps } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/AddSubConfiguratorsToTreeView.tsx";
import { useTranslation } from "react-i18next";

export const AddSubConfiguratorToTreeViewSingle = ({
	treeItem,
	closeDialog,
	onSubmit,
	addSubConfiguratorButtonSx,
	addSubConfiguratorButtonContextMenu,
}: AddSubConfiguratorsToTreeViewProps) => {
	const { t } = useTranslation();
	const [openContextMenu, contextMenuState] = useContextMenu();
	const usableSubConfiguratorComponents = getAddableTreeItemSubConfiguratorComponents(treeItem);

	if (usableSubConfiguratorComponents.length === 0) {
		return (
			<Card
				sx={{
					mt: 2,
					backgroundColor: "grey.50",
					border: "1px dashed",
					borderColor: "grey.500"
				}}
			>
				<CardContent sx={{ textAlign: "center", py: 4 }}>
					<Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
						{t("no_sub_configurations")}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t("select_or_add_sub_configuration")}
					</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				alignSelf: "stretch",
				display: "grid",
				gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
				alignItems: "stretch",
				gap: 2,
				mt: 2,
			}}
		>
			{usableSubConfiguratorComponents.map((subConfiguratorComponent) => (
				<Card
					key={subConfiguratorComponent.configurationComponentId}
					sx={{
						transition: "all 0.2s ease-in-out",
						cursor: "pointer",
						"&:hover": {
							transform: "translateY(-2px)",
							boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
							borderColor: "primary.main",
						},
						border: "1px solid",
						borderColor: "grey.300",
						borderRadius: 2,
					}}
				>
					<AsyncButton
						variant="text"
						fullWidth
						onClick={async () => {
							await onSubmit([subConfiguratorComponent]);
						}}
						onContextMenu={(event) => {
							if (addSubConfiguratorButtonContextMenu == null) return;
							openContextMenu({
								mouseEvent: event,
								content: addSubConfiguratorButtonContextMenu({
									component: subConfiguratorComponent,
									closeDialog: () => closeDialog(),
								}),
							});
						}}
						sx={mergeSx(
							{
								height: "100%",
								minHeight: 80,
								borderRadius: 2,
								textTransform: "none",
								"&:hover": {
									backgroundColor: "primary.50",
								},
							},
							addSubConfiguratorButtonSx?.(subConfiguratorComponent)
						)}
					>
						<CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
							<Typography
								variant="body1"
								sx={{
									textAlign: "center",
									fontWeight: 500,
									color: "text.primary",
									lineHeight: 1.3,
								}}
							>
								{subConfiguratorComponent.label}
							</Typography>
						</CardContent>
					</AsyncButton>
				</Card>
			))}
			<AavoContextMenu state={contextMenuState} />
		</Box>
	);
};
